import { rotatePt, bbox, polysIntersect, offsetPolygon, area, loops, loopRect } from "./geometry";
import { pairLayout, partsLayout, comboLayout } from "./pairs";

export const ROTATION_SETS = {
  quad: { label: "0° / 90° / 180° / 270°", angles: [0, 90, 180, 270] },
  ortho: { label: "0° / 90° only", angles: [0, 90] },
  fine: { label: "Any angle when it saves film", angles: Array.from({ length: 24 }, (_, i) => i * 15) },
  none: { label: "No rotation", angles: [0] },
};

const STEP = 1 / 16;
const COARSE = 0.5;
const NO_TURN = new Set(["nav_screen", "gauge_cluster"]); // screen protectors always show the way the customer looks at the screen (the roll runs left→right on screen, so "as drawn" is a 90° world turn) — never on their side
const UPRIGHT_COST = 6; // a set row turned on its side costs this much film before it beats staying upright
const ODD_ANGLE_COST = 1.0; // inches of roll an off-square angle must save before it beats an upright / 90° placement
const isOrtho = (rot) => rot % 90 === 0;

function collides(world, bb, placed, spacing) {
  for (const q of placed) {
    if (bb.minX > q.bb.maxX + spacing || bb.maxX < q.bb.minX - spacing || bb.minY > q.bb.maxY + spacing || bb.maxY < q.bb.minY - spacing) continue;
    for (const poly of q.inflated) if (polysIntersect(world, poly)) return true;
  }
  return false;
}

function attempt(rpts, rb, cx, cy, placed, spacing, margin, usableMax) {
  if (cy < margin - 1e-9 || cx < margin - 1e-9 || cx + rb.w > usableMax + 1e-9) return null;
  const dx = cx - rb.minX, dy = cy - rb.minY;
  const world = rpts.map(([x, y]) => [x + dx, y + dy]);
  const bb = { minX: cx, minY: cy, maxX: cx + rb.w, maxY: cy + rb.h };
  if (collides(world, bb, placed, spacing)) return null;
  return { x: cx, y: cy, world, bb };
}

// Gravity: start at the bbox "floor" of this column (everything above it is free by construction), then let the piece
// fall further polygon-aware so it tucks into the curves of the pieces below.
function drop(rpts, rb, cx, placed, spacing, margin, usableMax) {
  let floor = margin;
  for (const q of placed) if (cx < q.bb.maxX + spacing && cx + rb.w > q.bb.minX - spacing) floor = Math.max(floor, q.bb.maxY + spacing);
  let cur = attempt(rpts, rb, cx, floor, placed, spacing, margin, usableMax);
  if (!cur) return null;
  for (const step of [COARSE, STEP]) {
    let next;
    while ((next = attempt(rpts, rb, cur.x, cur.y - step, placed, spacing, margin, usableMax))) cur = next;
  }
  return cur;
}

function settle(rpts, rb, pos, placed, spacing, margin, usableMax) {
  let cur = pos, moved = true, guard = 0;
  while (moved && guard++ < 600) {
    moved = false;
    for (const [ddx, ddy] of [[0, -STEP], [-STEP, 0]]) {
      let next;
      while ((next = attempt(rpts, rb, cur.x + ddx, cur.y + ddy, placed, spacing, margin, usableMax))) {
        cur = next; moved = true;
        if (guard++ > 4000) break;
      }
    }
  }
  return cur;
}

// DigiCut blocks: door + fixed pane of one side sit along the belt line as on the car, Left & Right are stacked across the film
// (belt lines out); multi-part sets (3-piece back glass, 2-piece eyebrow) travel as one upright row, exactly as they sit on the vehicle.
const COMBOS = [["front_door", "front_vent"], ["rear_door", "quarter"]];
function buildItems(pieces, spacing, usableWidth) {
  const items = [], used = new Set(), upright = new Set();
  const key = (p) => `${p.pieceType || p.name}|${p.setKey || ""}`;
  const sets = new Map();
  for (const p of pieces) if (p.setKey && !p.loose) sets.set(p.setKey, [...(sets.get(p.setKey) || []), p]);  // loose = tinter split the set apart
  for (const list of sets.values()) {
    if (list.length < 2) continue;
    const lay = partsLayout(list, spacing, usableWidth);
    if (!lay || Math.min(lay.w, lay.h) > usableWidth + 1e-9) continue;
    list.forEach((p) => used.add(p.id));
    items.push({ ids: list.map((p) => p.id), points: lay.hull, members: lay.placed, worlds: lay.worlds, area: list.reduce((s, p) => s + area(p.points), 0), ...(lay.stacked ? { asDrawn: lay.h <= usableWidth + 1e-9 } : { upright: true }) });
  }
  const block = (list, lay) => {
    if (!lay || lay.h > usableWidth + 1e-9) { list.forEach((p) => upright.add(p.id)); return false; }  // doesn't sit across the film as drawn → the pieces go one after another, upright (owner's rule — never turned sideways)
    list.forEach((p) => used.add(p.id));
    items.push({ ids: list.map((p) => p.id), points: lay.hull, members: lay.placed, worlds: lay.worlds, area: list.reduce((s, p) => s + area(p.points), 0), asDrawn: true });
    return true;
  };
  const free = (type, side) => pieces.find((p) => !used.has(p.id) && !p.setKey && p.pieceType === type && p.side === side);
  for (const [door, pane] of COMBOS) {
    const L = [free(door, "left"), free(pane, "left")].filter(Boolean), R = [free(door, "right"), free(pane, "right")].filter(Boolean);
    if (L.length === 2 && R.length === 2) block(L.concat(R), comboLayout(L.concat(R), spacing, usableWidth));
    else if (L.length === 2 && !R.length) block(L, comboLayout(L, spacing, usableWidth));
    else if (R.length === 2 && !L.length) block(R, comboLayout(R, spacing, usableWidth));
  }
  for (const p of pieces) {
    if (used.has(p.id) || !["left", "right"].includes(p.side)) continue;
    const mate = pieces.find((q) => q.id !== p.id && !used.has(q.id) && key(q) === key(p) && q.side && q.side !== p.side && ["left", "right"].includes(q.side));
    if (mate) block([p, mate], pairLayout(p.points, mate.points, spacing));
  }
  for (const p of pieces) if (!used.has(p.id)) { const pts = p.multi ? loopRect(loops(p)) : p.points; items.push({ ids: [p.id], points: pts, members: [{ rot: 0, x: 0, y: 0 }], worlds: [pts], area: area(pts), ...(upright.has(p.id) ? { asDrawn: true } : {}), ...(NO_TURN.has(p.pieceType) ? { noTurn: true } : {}) }); }  // multi-part decals travel as one block; screens never lie sideways
  return items.sort((a, b) => b.area - a.area);
}

export function nestPieces(pieces, { rollWidth, spacing, margin, rotations, strictRotations = false }, fixed = []) {
  const t0 = performance.now();
  const usableMax = rollWidth - margin;
  const usableWidth = rollWidth - 2 * margin;
  const items = buildItems(pieces, spacing, usableWidth);
  const placed = fixed.map((f) => ({ bb: bbox(f.world), inflated: [offsetPolygon(f.world, spacing)], fixed: true }));
  const overflow = [], out = [];
  let rollLen = placed.reduce((m, q) => Math.max(m, q.bb.maxY), margin);
  const angles = strictRotations ? [...rotations] : [...new Set([0, 180, 90, 270, ...rotations].filter((a) => rotations.includes(a) || a === 0))];
  const colStep = Math.max(0.5, usableWidth / 32);

  for (const it of items) {
    let best = null;
    for (const rot of it.noTurn ? [90] : it.upright ? [0, 90, 270] : it.asDrawn === undefined ? angles : it.asDrawn ? [90, 270] : [0, 180]) {  // set rows: upright or turned along the film when too wide; DigiCut blocks: as drawn, turned only when they don't fit across
      const rpts = it.points.map((p) => rotatePt(p, rot));
      const rb = bbox(rpts);
      if (rb.w > usableWidth + 1e-9) continue;
      const cols = [];
      for (let cx = margin; cx + rb.w <= usableMax + 1e-9; cx += colStep) cols.push(cx);
      cols.push(usableMax - rb.w);
      for (const q of placed) { cols.push(q.bb.maxX + spacing); cols.push(q.bb.minX - spacing - rb.w); }
      for (const cx of cols) {
        if (cx < margin - 1e-9 || cx + rb.w > usableMax + 1e-9) continue;
        const pos = drop(rpts, rb, cx, placed, spacing, margin, usableMax);
        if (!pos) continue;
        const newLen = Math.max(rollLen, pos.bb.maxY) + (isOrtho(rot) ? 0 : ODD_ANGLE_COST) + (it.upright && rot !== 0 ? UPRIGHT_COST + (rot === 270 ? 0.01 : 0) : 0);
        const score = newLen * 1e6 + pos.bb.maxY * 1e3 + pos.x;
        if (!best || score < best.score) best = { score, rot, rpts, rb, pos };
      }
    }
    if (!best) { overflow.push(...it.ids); continue; }
    const pos = settle(best.rpts, best.rb, best.pos, placed, spacing, margin, usableMax);
    const X = pos.x - best.rb.minX, Y = pos.y - best.rb.minY, R = best.rot;
    const worlds = it.worlds.map((w) => w.map((p) => { const [x, y] = rotatePt(p, R); return [x + X, y + Y]; }));
    placed.push({ bb: pos.bb, inflated: worlds.map((w) => offsetPolygon(w, spacing)) });
    rollLen = Math.max(rollLen, pos.bb.maxY);
    it.ids.forEach((id, i) => {
      const m = it.members[i];
      const [ox, oy] = rotatePt([m.x, m.y], R);
      out.push({ id, rotation: (m.rot + R) % 360, x: X + ox, y: Y + oy });
    });
  }

  const usedLen = out.length || fixed.length ? rollLen + margin : 0;
  const totalArea = items.filter((it) => !it.ids.some((id) => overflow.includes(id))).reduce((s, it) => s + it.area, 0);
  return {
    placements: out,
    rollLength: usedLen,
    areaUsed: totalArea,
    utilization: usedLen ? totalArea / (usedLen * rollWidth) : 0,
    overflow,
    ms: Math.round(performance.now() - t0),
  };
}
